import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Globe2,
  Mail,
  Phone,
  TrendingUp,
  Calendar,
  User,
  Lock,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';
import { Stepper } from '../../components/Stepper';
import { OptionCard } from '../../components/OptionCard';
import { Card } from '../../components/Card';
import { Banner } from '../../components/Banner';
import { cn } from '../../lib/cn';
import { formatDate } from '../../lib/format';
import { useResellerApplications } from '../../api/admin';
import { useSignupAvailability } from '../../api/hooks';
import type { ResellerApplication } from '../../mocks/admin';
import { useSession } from '../../stores/session';
import { submitResellerApplication, type ResellerApplicationBody } from '../../api/reseller';

const STEPS = ['Organization', 'Contact', 'Why us', 'Review'];

const VOLUMES: Array<{ value: number; label: string; sub: string }> = [
  { value: 50_000, label: 'Under ৳50k / month', sub: 'Just getting started' },
  { value: 200_000, label: '৳50k – ৳200k / month', sub: 'Established small operation' },
  { value: 500_000, label: '৳200k – ৳500k / month', sub: 'Multi-customer pipeline' },
  { value: 1_000_000, label: '৳500k+ / month', sub: 'Established reseller / agency' },
];

const COUNTRIES = [
  { code: 'BD', label: 'Bangladesh' },
  { code: 'IN', label: 'India' },
  { code: 'PK', label: 'Pakistan' },
  { code: 'LK', label: 'Sri Lanka' },
  { code: 'NP', label: 'Nepal' },
  { code: 'XX', label: 'Other' },
];

type Mode = 'anonymous' | 'existing';

const TODAY_ISO = '2026-05-03';

function isFutureDate(iso?: string): boolean {
  if (!iso) return false;
  return new Date(iso) > new Date(TODAY_ISO);
}

/**
 * /become-a-reseller — public reseller application form.
 * Multi-step wizard: Organization → Contact → Why us → Review.
 *
 * Mirrors the Signup.tsx pattern (Stepper, OptionCard, Card-wrapped form).
 * On submit it simulates a 1.2s network call then routes to
 * /become-a-reseller/submitted?id=app_new — the mocks array is NOT mutated.
 */
export function BecomeAReseller() {
  const navigate = useNavigate();
  const { data: resellerApplications = [] } = useResellerApplications();
  const { data: signupAvail } = useSignupAvailability();
  const user = useSession((s) => s.user);
  const [step, setStep] = useState(0);

  // Demo-only toggle for "logged-in" simulation. In real product this would
  // come from auth state.
  const [mode, setMode] = useState<Mode>('anonymous');

  // step 0 — organization
  const [orgName, setOrgName] = useState('');
  const [orgWebsite, setOrgWebsite] = useState('');
  const [country, setCountry] = useState('BD');
  const [yearsInBusiness, setYearsInBusiness] = useState<string>('');
  const [expectedVolumeMinor, setExpectedVolumeMinor] = useState<number>(VOLUMES[1]!.value);

  // step 1 — contact
  const [applicantName, setApplicantName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  // step 2 — why us
  const [whyUs, setWhyUs] = useState('');
  const [references, setReferences] = useState('');

  // step 3 — review
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Mode-driven prefill ──
  // We re-derive on each render so the toggle doesn't have to imperatively
  // overwrite state — but only fill when the relevant field is empty so a
  // user's typed values aren't trampled when they flip the toggle.
  function setMode_(next: Mode) {
    setMode(next);
    if (next === 'existing') {
      // Pre-fill from currentUser. currentUser doesn't carry a phone;
      // we fall back to a sensible BD-format placeholder.
      if (!applicantName) setApplicantName(user?.name ?? '');
      if (!email) setEmail(user?.email ?? '');
      if (!phone) setPhone('+8801717540035');
      setPassword('');
    }
  }

  // ── Email-collision detection (runs after blur on email field) ──
  const matchedApplication: ResellerApplication | undefined = useMemo(() => {
    if (!emailTouched) return undefined;
    const normalized = email.trim().toLowerCase();
    if (!normalized) return undefined;
    return resellerApplications.find(
      (a) => a.email.toLowerCase() === normalized,
    );
  }, [email, emailTouched, resellerApplications]);

  type CollisionState =
    | { kind: 'block_pending'; app: ResellerApplication }
    | { kind: 'block_rejected_cooldown'; app: ResellerApplication }
    | { kind: 'info_reapply_ok'; app: ResellerApplication }
    | { kind: 'success_already_approved'; app: ResellerApplication }
    | null;

  const collision: CollisionState = useMemo(() => {
    if (!matchedApplication) return null;
    const a = matchedApplication;
    if (a.status === 'pending' || a.status === 'in_review' || a.status === 'more_info_needed') {
      return { kind: 'block_pending', app: a };
    }
    if (a.status === 'rejected') {
      if (isFutureDate(a.reapplyAfter)) {
        return { kind: 'block_rejected_cooldown', app: a };
      }
      return { kind: 'info_reapply_ok', app: a };
    }
    if (a.status === 'withdrawn') {
      return { kind: 'info_reapply_ok', app: a };
    }
    if (a.status === 'approved') {
      return { kind: 'success_already_approved', app: a };
    }
    return null;
  }, [matchedApplication]);

  const isBlockedByCollision =
    collision?.kind === 'block_pending' || collision?.kind === 'block_rejected_cooldown';

  // ── Per-step validity ──
  function isStepValid(s: number): boolean {
    if (s === 0) {
      const yrs = Number(yearsInBusiness);
      return (
        orgName.trim().length > 1 &&
        country.length > 0 &&
        yearsInBusiness !== '' &&
        Number.isFinite(yrs) &&
        yrs >= 0 &&
        expectedVolumeMinor > 0
      );
    }
    if (s === 1) {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      const phoneOk = phone.startsWith('+') && phone.replace(/\D/g, '').length >= 9;
      const nameOk = applicantName.trim().length > 1;
      const passwordOk = mode === 'existing' || password.length >= 8;
      return nameOk && emailOk && phoneOk && passwordOk && !isBlockedByCollision;
    }
    if (s === 2) {
      return whyUs.trim().length >= 50 && whyUs.trim().length <= 500;
    }
    if (s === 3) {
      return acceptedTerms;
    }
    return false;
  }

  function next() {
    if (step < STEPS.length - 1 && isStepValid(step)) setStep((s) => s + 1);
  }
  function back() {
    if (step > 0) setStep((s) => s - 1);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (step < STEPS.length - 1) {
      next();
      return;
    }
    if (!isStepValid(step)) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await submitResellerApplication(
        toApplicationPayload({
          orgName, applicantName, email, phone, country,
          orgWebsite, expectedVolumeMinor, whyUs, references,
        }),
      );
      navigate({
        to: '/become-a-reseller/submitted' as never,
        search: { id: created.id } as never,
      });
    } catch (err) {
      setSubmitting(false);
      setSubmitError(
        err instanceof Error ? err.message : 'Something went wrong submitting your application. Please try again.',
      );
    }
  }

  const selectedVolumeLabel =
    VOLUMES.find((v) => v.value === expectedVolumeMinor)?.label ?? '';
  const selectedCountryLabel =
    COUNTRIES.find((c) => c.code === country)?.label ?? country;

  if (signupAvail && !signupAvail.resellerApplications) {
    return (
      <div className="min-h-screen bg-marketing dark:bg-marketing-dark grid place-items-center px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <h1 className="text-xl font-semibold text-zinc-950 dark:text-zinc-50">Reseller applications are closed</h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            We're not accepting new reseller applications right now. Please check back later or contact us.
          </p>
          <Link to="/login" className="mt-5 inline-block text-sm font-medium text-brand dark:text-brand-dm hover:underline">
            Back to sign in
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark">
      <header className="px-4 sm:px-8 py-5 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700 gap-4 flex-wrap">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Possignin
          </span>
        </Link>
        <div className="flex items-center gap-3 flex-wrap">
          <ModeChip mode={mode} onChange={setMode_} />
          <div className="text-xs text-zinc-500 dark:text-zinc-400">
            Already a partner?{' '}
            <Link
              to="/reseller/login"
              className="font-medium text-brand dark:text-brand-dm hover:underline"
            >
              Reseller login
            </Link>
          </div>
          <AuthThemeToggle />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Become a Possignin reseller
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            Sell Possignin under your own brand. We review every application within 1–2 business days.
          </p>
        </div>

        <div className="mb-6 flex justify-center">
          <Stepper steps={STEPS} current={step} />
        </div>

        {/* Mode banner */}
        {mode === 'existing' ? (
          <Banner tone="info" className="mb-6">
            <span>
              Welcome back, <strong>{user?.name}</strong>. We'll attach this application
              to your existing account ({user?.email}).
            </span>
          </Banner>
        ) : (
          <Banner tone="info" className="mb-6">
            <span>
              Use an email you control — we'll send the decision (and, if approved,
              setup instructions) there.
            </span>
          </Banner>
        )}

        <Card className="p-5 sm:p-8">
          <form onSubmit={onSubmit} className="space-y-6">
            {submitError && (
              <Banner tone="error" title="Couldn’t submit your application">
                {submitError}
              </Banner>
            )}
            {/* ── Step 0: Organization ── */}
            {step === 0 && (
              <div className="space-y-5">
                <Input
                  label="Organization name"
                  required
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Acme Resellers Ltd."
                  prefix={<Building2 className="w-4 h-4" />}
                />
                <Input
                  label="Website"
                  type="url"
                  value={orgWebsite}
                  onChange={(e) => setOrgWebsite(e.target.value)}
                  placeholder="https://acme-resellers.com"
                  prefix={<Globe2 className="w-4 h-4" />}
                  hint="Optional — a landing page or LinkedIn page is fine."
                />

                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                    Country
                  </label>
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className={cn(
                      'block w-full px-3 py-2.5 text-sm rounded-md transition',
                      'bg-white text-zinc-950',
                      'border border-zinc-300',
                      'focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
                      'dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700',
                      'dark:focus:ring-brand-dm dark:focus:border-brand-dm',
                    )}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Years your organization has been operating"
                  type="number"
                  min={0}
                  max={50}
                  required
                  value={yearsInBusiness}
                  onChange={(e) => setYearsInBusiness(e.target.value)}
                  placeholder="3"
                  prefix={<Calendar className="w-4 h-4" />}
                  hint="Approximate is fine. Use 0 if your organization is brand new."
                />

                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                    Expected monthly volume
                    <span className="text-rose-600 ml-0.5">*</span>
                  </label>
                  <p className="mb-3 text-xs text-zinc-500 dark:text-zinc-400">
                    Roughly how much revenue do you expect to bring through Possignin once
                    you're up and running?
                  </p>
                  <div className="space-y-2.5">
                    {VOLUMES.map((v) => (
                      <OptionCard
                        key={v.value}
                        selected={expectedVolumeMinor === v.value}
                        onSelect={() => setExpectedVolumeMinor(v.value)}
                      >
                        <div className="flex items-start gap-3">
                          <TrendingUp className="w-4 h-4 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-zinc-950 dark:text-zinc-50">
                              {v.label}
                            </div>
                            <div className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-300">
                              {v.sub}
                            </div>
                          </div>
                        </div>
                      </OptionCard>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 1: Contact ── */}
            {step === 1 && (
              <div className="space-y-5">
                <Input
                  label="Your full name"
                  required
                  value={applicantName}
                  onChange={(e) => setApplicantName(e.target.value)}
                  placeholder="Rakib Ahmed"
                  prefix={<User className="w-4 h-4" />}
                />
                <Input
                  label="Email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder="you@your-org.com"
                  autoComplete="email"
                  prefix={<Mail className="w-4 h-4" />}
                  error={
                    collision?.kind === 'block_pending'
                      ? `We already have an application from this email submitted on ${formatDate(
                          collision.app.submittedAt,
                        )}. Status: ${humanizeStatus(collision.app.status)}.`
                      : collision?.kind === 'block_rejected_cooldown'
                      ? `We can't accept a new application yet. Last decision: ${
                          collision.app.decisionReason ?? 'declined'
                        }. You can re-apply on ${formatDate(collision.app.reapplyAfter ?? '')}.`
                      : undefined
                  }
                />

                {/* Inline action when blocked-pending: jump to status check */}
                {collision?.kind === 'block_pending' && (
                  <div className="-mt-3 flex justify-end">
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      iconRight={<ArrowRight className="w-3.5 h-3.5" />}
                      onClick={() =>
                        navigate({
                          to: '/become-a-reseller/status' as never,
                          search: { email: collision.app.email } as never,
                        })
                      }
                    >
                      Check application status
                    </Button>
                  </div>
                )}

                {collision?.kind === 'info_reapply_ok' && (
                  <Banner tone="info">
                    You previously applied on {formatDate(collision.app.submittedAt)} (
                    {humanizeStatus(collision.app.status)}). Submitting again is fine.
                  </Banner>
                )}

                {collision?.kind === 'success_already_approved' && (
                  <Banner
                    tone="success"
                    title="You're already approved"
                    action={
                      <Button
                        variant="secondary"
                        size="sm"
                        type="button"
                        onClick={() => navigate({ to: '/reseller/login' })}
                      >
                        Sign in
                      </Button>
                    }
                  >
                    Your previous application was approved. Sign in to finish setup.
                  </Banner>
                )}

                <Input
                  label="Mobile / WhatsApp"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+8801XXXXXXXXX"
                  autoComplete="tel"
                  prefix={<Phone className="w-4 h-4" />}
                  hint="Must start with + and country code."
                  error={
                    phone && !phone.startsWith('+')
                      ? 'Phone number must start with + and country code (e.g. +8801…)'
                      : undefined
                  }
                />

                {mode === 'anonymous' && (
                  <Input
                    label="Password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    prefix={<Lock className="w-4 h-4" />}
                    hint="You'll use this to check your application status. We'll activate the account on approval."
                  />
                )}
              </div>
            )}

            {/* ── Step 2: Why us ── */}
            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                    Why do you want to resell Possignin?
                    <span className="text-rose-600 ml-0.5">*</span>
                  </label>
                  <textarea
                    value={whyUs}
                    onChange={(e) => setWhyUs(e.target.value)}
                    rows={6}
                    maxLength={500}
                    placeholder="Tell us about your existing customer base, why Possignin fits your portfolio, and how you'd reach businesses in your region."
                    className={cn(
                      'block w-full px-3 py-2.5 text-sm rounded-md transition resize-y',
                      'bg-white text-zinc-950 placeholder-zinc-400',
                      'border border-zinc-300',
                      'focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
                      'dark:bg-zinc-900 dark:text-zinc-50 dark:placeholder-zinc-500',
                      'dark:border-zinc-700',
                      'dark:focus:ring-brand-dm dark:focus:border-brand-dm',
                    )}
                  />
                  <div className="mt-1.5 flex items-center justify-between text-xs">
                    <span
                      className={cn(
                        whyUs.trim().length < 50
                          ? 'text-zinc-500 dark:text-zinc-400'
                          : 'text-emerald-700 dark:text-emerald-400',
                      )}
                    >
                      {whyUs.trim().length < 50
                        ? `Minimum 50 characters (${50 - whyUs.trim().length} more)`
                        : 'Looks good'}
                    </span>
                    <span className="tabular-nums text-zinc-500 dark:text-zinc-400">
                      {whyUs.length} / 500
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                    References
                    <span className="ml-1 text-xs font-normal text-zinc-500 dark:text-zinc-400">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    value={references}
                    onChange={(e) => setReferences(e.target.value)}
                    rows={3}
                    maxLength={300}
                    placeholder="Anyone we can talk to? Existing customers, partners, vendors you work with."
                    className={cn(
                      'block w-full px-3 py-2.5 text-sm rounded-md transition resize-y',
                      'bg-white text-zinc-950 placeholder-zinc-400',
                      'border border-zinc-300',
                      'focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
                      'dark:bg-zinc-900 dark:text-zinc-50 dark:placeholder-zinc-500',
                      'dark:border-zinc-700',
                      'dark:focus:ring-brand-dm dark:focus:border-brand-dm',
                    )}
                  />
                </div>
              </div>
            )}

            {/* ── Step 3: Review ── */}
            {step === 3 && (
              <div className="space-y-5">
                <ReviewBlock title="Organization">
                  <ReviewRow k="Name" v={orgName || '—'} />
                  {orgWebsite && <ReviewRow k="Website" v={orgWebsite} />}
                  <ReviewRow k="Country" v={selectedCountryLabel} />
                  <ReviewRow k="Years operating" v={yearsInBusiness || '—'} />
                  <ReviewRow k="Expected monthly volume" v={selectedVolumeLabel} />
                </ReviewBlock>

                <ReviewBlock title="Contact">
                  <ReviewRow k="Name" v={applicantName} />
                  <ReviewRow k="Email" v={email} />
                  <ReviewRow k="Phone" v={phone} />
                  <ReviewRow
                    k="Account"
                    v={
                      mode === 'existing'
                        ? 'Linked to your existing Possignin account'
                        : 'New account (password set)'
                    }
                  />
                </ReviewBlock>

                <ReviewBlock title="Why us">
                  <p className="text-sm text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap">
                    {whyUs}
                  </p>
                  {references && (
                    <>
                      <div className="mt-3 text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-medium">
                        References
                      </div>
                      <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap">
                        {references}
                      </p>
                    </>
                  )}
                </ReviewBlock>

                <label className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  <span>
                    I've read the{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      className="text-brand dark:text-brand-dm hover:underline inline-flex items-center gap-1"
                    >
                      partner terms <ExternalLink className="w-3 h-3" />
                    </Link>{' '}
                    and confirm the information above is accurate.
                  </span>
                </label>
              </div>
            )}

            {/* ── Footer ── */}
            <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
              {step > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={back}
                  iconLeft={<ArrowLeft className="w-4 h-4" />}
                >
                  Back
                </Button>
              ) : (
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cancel
                </Link>
              )}

              <Button
                type="submit"
                loading={submitting}
                disabled={!isStepValid(step)}
                iconRight={
                  !submitting && step < STEPS.length - 1 ? (
                    <ArrowRight className="w-4 h-4" />
                  ) : undefined
                }
              >
                {step < STEPS.length - 1 ? 'Continue' : 'Submit application'}
              </Button>
            </div>
          </form>
        </Card>

        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <AlertCircle className="w-3 h-3" />
          We respond within 1–2 business days. No payment required at this stage.
        </p>
      </main>
    </div>
  );
}

/* ── helpers ────────────────────────────────────────────────────────────── */

/** The wizard's collected fields (the subset the application API accepts). */
export interface ResellerApplicationFormState {
  orgName: string;
  applicantName: string;
  email: string;
  phone: string;
  country: string;
  orgWebsite: string;
  expectedVolumeMinor: number;
  whyUs: string;
  references: string;
}

/** Map the wizard's form state to the public reseller-application API body. */
export function toApplicationPayload(f: ResellerApplicationFormState): ResellerApplicationBody {
  const trimOr = (s: string): string | undefined => {
    const t = s.trim();
    return t.length > 0 ? t : undefined;
  };
  return {
    orgName: f.orgName.trim(),
    applicantName: f.applicantName.trim(),
    email: f.email.trim(),
    phone: trimOr(f.phone),
    country: f.country || undefined,
    website: trimOr(f.orgWebsite),
    expectedVolumeMinor: f.expectedVolumeMinor,
    whyUs: trimOr(f.whyUs),
    referencesText: trimOr(f.references),
  };
}

function ModeChip({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return (
    <div
      className={cn(
        'inline-flex items-center rounded-full p-0.5 text-xs',
        'bg-zinc-100 dark:bg-zinc-800',
      )}
      role="radiogroup"
      aria-label="Demo mode"
    >
      <span className="px-2 text-zinc-500 dark:text-zinc-400 font-medium">Demo:</span>
      <button
        type="button"
        role="radio"
        aria-checked={mode === 'anonymous'}
        onClick={() => onChange('anonymous')}
        className={cn(
          'px-2.5 py-1 rounded-full font-medium transition',
          mode === 'anonymous'
            ? 'bg-white text-zinc-950 shadow-xs dark:bg-zinc-700 dark:text-zinc-50'
            : 'text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50',
        )}
      >
        Anonymous
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={mode === 'existing'}
        onClick={() => onChange('existing')}
        className={cn(
          'px-2.5 py-1 rounded-full font-medium transition',
          mode === 'existing'
            ? 'bg-white text-zinc-950 shadow-xs dark:bg-zinc-700 dark:text-zinc-50'
            : 'text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50',
        )}
      >
        Already a customer
      </button>
    </div>
  );
}

function ReviewBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800/40 p-4 sm:p-5">
      <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-medium mb-3">
        {title}
      </div>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  );
}

function ReviewRow({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <dt className="text-zinc-500 dark:text-zinc-400 flex-shrink-0">{k}</dt>
      <dd className="text-zinc-950 dark:text-zinc-50 text-right break-words min-w-0">{v}</dd>
    </div>
  );
}

function humanizeStatus(s: ResellerApplication['status']): string {
  const map: Record<ResellerApplication['status'], string> = {
    pending: 'Pending review',
    in_review: 'In review',
    more_info_needed: 'More info needed',
    approved: 'Approved',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn',
  };
  return map[s];
}
